/**
 * @file LoginFormView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import { Backdrop, Box, Button, Dialog, DialogContent, Grid, IconButton, InputAdornment, InputLabel, OutlinedInput, Paper, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import React from "react";
import "./../Login.css"
import { Navigate } from "react-router-dom";
import { SignButtonTheme, SignLabelTheme, SsoLabelTheme } from "../LoginTheme";
import { LoginFormViewModel } from "../viewmodels/LoginFormViewModel";
import Snackbar from "@mui/material/Snackbar";
import { AppProgressBar } from "../../utils/AppProgressBar";
import { AppAlert } from "../../utils/AppAlert";
import { SelectClinicView } from "./SelectClinicView";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FormControl from "@mui/material/FormControl";
import { CSSTransition } from "react-transition-group";
import { LocationComponent } from "../../utils/LocationComponent";
import { RoleType } from "../utils/RoleType";
import { Clinic } from "../../../services/models/Clinic";
import { ApiError } from "../../../services/models/ApiError";
import { BaseComponent } from "../../home/base/BaseComponent";
import { TextFieldWrapper } from "../../common/customviews/TextFieldWrapper";
import { AppUtility } from "../../utils/AppUtility";
import { roboto500Text16 } from "../../utils/theme/CommonTheme";
import HomeConsentDialog from "../../common/customviews/HomeConsentDialog";
import { DialogTransition } from "../../common/customviews/DialogTransition";
import { ConsentStatus } from "../utils/ConsentStatus";
import { NotAllowedToUsePortal } from "../../utils/AppConstant";

/**
 * An interface definition for LoginError
 */
interface LoginError {
    code: string;
    message: string;
    type: string
}

/**
 * A type of Component state
 */
type CompState = {
    errorToggle: boolean, loading: boolean, submitted: boolean, errorMessage: string, username: string, showConsent: boolean,
    password: string, isSignInDisabled: boolean, emailValidationError: string, loginState: LoginPageState, showPassword: boolean, authToken: string
}

/**
 * An interface definition for Component Props
 */
interface Props {
    viewModel: LoginFormViewModel,
    onForgotPasswordClincked: any 
}

/**
 * An enum definition for LoginPageState
 */
enum LoginPageState {
    PreLogin = "PreLogin",
    LoggedIn = "LoggedIn",
    PreConsent = "PreConsent",
    Consent = "Consent",
    PostConsent = "PostLogin"
}

/**
 * A class definition for LoginFormView
 */
export class LoginFormView extends BaseComponent<Props, CompState> {

    location?: Location;

    constructor(props: Props) {
        super(props)
        
        this.state = {
            errorToggle: false, loading: false, submitted: false, showConsent: false,
            errorMessage: "", username: "", password: "", isSignInDisabled: true,
            emailValidationError: "", loginState: LoginPageState.PreLogin, showPassword: false, authToken: ""
        };
    }

    /**
     * Handles click show password
     */
    private handleClickShowPassword = () => {
        this.setState({ showPassword: !this.state.showPassword })
    }

    /**
     * Handles mouse down password
     * @param event React.MouseEvent
     */
    private handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    /**
     * Toggle loading
     */
    private toggleLoading = (loading: boolean) => {
        this.setState({ loading: loading })
    }

    /**
     * Trigger on forgot password click
     */
    private onForgotPasswordClicked = () => {
        this.props.onForgotPasswordClincked();
    }

    /**
     * Sets the email error
     */
    private toggleEmailError = (emailValidationError: string) => {
        this.setState({ emailValidationError: emailValidationError })
    }

    /**
     * Toggle error
     * @param errorMessage string
     */
    private toggleError = (show: boolean, errorMessage: string) => {
        this.logger.error(`ToggleError (${errorMessage})`, LoginFormView.name);
        show ? this.setState({ errorToggle: true, errorMessage: errorMessage, loading: false }) : this.setState({ errorToggle: false, errorMessage: errorMessage, loading: false })
    }

    /**
     * Triggers on the change of username
     * @param username string
     */
    private onChangeUsername = (username: string) => {
        const isValidEmail = AppUtility.validateEmail(username);
        const disabled = isValidEmail && this.state.password.trim().length > 0;

        isValidEmail && username.trim().length > 0 ? this.toggleEmailError("") : this.toggleEmailError("Please enter a valid email address");
        this.setCrendentials({ username: username, isSignInDisabled: !disabled })
    }

    /**
     * Triggers on the change of password
     * @param password string
     */
    private onChangePassword = (password: string) => {
        const isValidEmail = AppUtility.validateEmail(this.state.username);
        const disabled = isValidEmail && password.trim().length > 0;
        this.setCrendentials({ password: password, isSignInDisabled: !disabled })
    }

    /**
     * Sets credentials
     * @param json any
     */
    private setCrendentials = (json: any) => {
        this.setState(json);
    }

    /**
     * Submits credentials
     * @returns 
     */
    private submitCredentials = async () => {
            if(!navigator.onLine){ // TODO : check why internet is not working
                this.toggleError(true, "No Internet Connection");
                return;
            }
            this.toggleLoading(true);
            this.props.viewModel.login(this.state.username, this.state.password);            
    }

    /**
     * Handles consent flow, API calls etc.
     */
    private handlePreConsentFlow = ()=> {
        this.setState({
            loginState: LoginPageState.PreConsent,
            loading : true
        });
        this.logger.warning('handlePreConsentFlow()', LoginFormView.name);
        const accessToken = this.validateAndGetSession(LoginFormView.name);
        this.props.viewModel.getUserConsents(accessToken, {});
    }

    /**
     * Handles consent flow, API calls etc.
     */
    private handleConsentFlow = ()=> {
        this.setState({
            loginState: LoginPageState.Consent,
            showConsent : true
        });
    }
    
    /**
     * Handles privacy notice
     * @param status ConsentStatus
     * @returns 
     */
    private handleNoticeNotice= (status: ConsentStatus)=> {
        if(status === ConsentStatus.Decline) {
            // exit with a dialog
            this.handleDecline();
            return;
        }
        
        this.setState({
            loginState: LoginPageState.Consent,
            showConsent : false
        });
            
        this.loadClinics(this.getLoggedInUserRole().includes(RoleType.BSCRep) ? RoleType.BSCRep : RoleType.HCP);
    }

    /**
     * Handles decline
     */
    private handleDecline () {
        // exit with a dialog
        this.setState({
            loginState: LoginPageState.PreLogin,
            showConsent : false,
            errorToggle: true, errorMessage : NotAllowedToUsePortal
        });
        this.logOut();
    }

    private subscribeApiEvents = ()=>{
        this.props.viewModel.onLoadAuthorizeURL.subscribe((ssoURL: string)=>{
            window.location.href = ssoURL;
        })

        this.props.viewModel.onLoadAuthorizeURLError.subscribe((apiError: ApiError)=>{
            this.toggleLoading(false);
            this.toggleError(true, apiError.message !== undefined ? apiError.message : "Error while starting sso sign in");
        })
        this.props.viewModel.onUserConsentLoaded.subscribe((userConsentResponse : any)=>{
            // Handle if user has accepted both consents
            this.toggleLoading(false);
            if(userConsentResponse && !userConsentResponse.status) {
                this.logger.verbose('onUserConsentLoaded - User not consented', LoginFormView.name);
                this.changeLoginState(LoginPageState.PreConsent);
                this.handleConsentFlow();
            } else {
                this.logger.verbose('onUserConsentLoaded - User consented', LoginFormView.name);
                this.changeLoginState(LoginPageState.LoggedIn);
                this.loadClinics(this.getLoggedInUserRole().includes(RoleType.BSCRep) ? RoleType.BSCRep : RoleType.HCP);
            }

        });

        this.props.viewModel.onUserConsentLoadedError.subscribe((apiError: ApiError)=>{
            this.changeLoginState(LoginPageState.Consent);
            this.toggleLoading(false);
            this.toggleError(true, apiError.message !== undefined ? apiError.message : "Error while loading consents");
        });

        this.props.viewModel.onLoggedIn.subscribe(userToken=> {
            this.toggleError(false, '');
            this.toggleLoading(false);
            this.logger.warning(`submitCredentials() AcccessToken ${userToken}`, LoginFormView.name);
            this.setSesionToken(userToken);
            this.setLoggedInUserRole(RoleType.HCP);
            this.handlePreConsentFlow();
        });

        this.props.viewModel.onLoggedInError.subscribe(apiError=>{
            this.toggleError(true, apiError.message !== undefined ? apiError.message : "Invalid password or login name");
            this.toggleLoading(false);
        });
    }

    /**
     * Loads clinics
     * @param role RoleType
     * @returns 
     */
    private loadClinics= (role: RoleType) => {
        this.changeLoginState(LoginPageState.LoggedIn);

        this.props.viewModel.onClinicLoaded.subscribe((clinics : Clinic[])=>{

            if(role === RoleType.BSCRep) {
                this.setAllClinics(clinics);
                this.changeLoginState(LoginPageState.PostConsent);
                this.setLoggedInUserRole(role);
                return;
            }
            if(clinics.length <= 0){
                this.changeLoginState(LoginPageState.PreLogin);
                this.toggleLoading(false);
                this.toggleError(true, "Unfortunately, HCP is not assigned to any clinic");
                return;
            }

            if(role== RoleType.HCP) {
                this.setIsLoggedIn(true);
                this.setAllClinics(clinics);
                this.setSelectedClinic(clinics[0]);
                this.setLoggedInUserRole(role);
                this.navigateToHome();
            }
        });

        this.props.viewModel.onClinicError.subscribe((apiError: ApiError)=>{
            this.changeLoginState(LoginPageState.PreLogin);
            this.toggleLoading(false);
            this.toggleError(true, apiError.message !== undefined ? apiError.message : "Error while loading clinics");
        })
        const accessToken = this.validateAndGetSession(LoginFormView.name);
        // Load clinics
        this.props.viewModel.getClinics(accessToken); // eslint-disable-line
        
    }

    /**
     * Navigates to home
     */
    private navigateToHome = () => {
        this.setState({
            submitted: true
        })
    }

    /**
     * Changes the LoginPageState
     * @param currentLoginState LoginPageState
     */
    private changeLoginState = (currentLoginState: LoginPageState) => {
        this.setState({
            loginState: currentLoginState
        })
    }

    /**
     * Handles Locatoin updates
     * @param location Location
     */
    private handleLocationUpdate = (location: Location) => {
        this.location = location;
    }

    /**
     * Makes a request to get the SAML2 authentication link for Single Sign On
     */
    private authenticate = async () => {
            this.toggleLoading(true);
            this.props.viewModel.getAuthorizeURL();
    }

    /**
     * Triggers on the selection of clinic
     * @param clinic Clinic
     */
    public onClinicSelected = (clinic: Clinic) => {
        this.logger.verbose(`onClinicSelected(${JSON.stringify(clinic)})`, LoginFormView.name);
        this.setSesionToken(this.state.authToken);
        this.setIsLoggedIn(true);
        this.setSelectedClinic(clinic);
        this.navigateToHome();
    }

    async componentDidMount() {
        this.subscribeApiEvents();
        await this.created();
    }

    /**
     * use Query
     * @returns URLSearchParams
     */
    useQuery = () => new URLSearchParams(window.location?.search);

    /**
     * Gets AuthToken
     * @param query URLSearchParams
     * @returns 
     */
    private getEncodedQueryArgs = (query: URLSearchParams, key: string) => query.get(key);

    /**
     * Sets auth token
     * @param authToken string
     */
    private setAuthToken = (authToken: string)=> {
        this.setState({
            authToken: authToken
        });
    }

    /**
     * Deletes
     * @param query 
     */
    private deleteQuery = (query) => {
        query.delete("error");
        query.delete("authToken");
        window?.history.replaceState({}, "" ,"/");
    }

    /**
     * Calls when components is mounted
     * @returns 
     */
    private created = async () => {
        this.logger.warning('Checking for existing login....', LoginFormView.name);
        if (this.isLoggedIn()) {  // Existing token present, go to home page
            this.navigateToHome();
            return;
        }

        const query = this.useQuery();
        const encodedAuthToken = this.getEncodedQueryArgs(query, "authToken");
        const encodedError = this.getEncodedQueryArgs(query, "error");

        if (!encodedAuthToken && !encodedError) {
            this.logger.warning("Fresh Page opened", LoginFormView.name);
            return;
        }

        this.logger.verbose(`created(${encodedAuthToken}) Error-> ${encodedError}`, LoginFormView.name);
        this.deleteQuery(query);

        if (encodedError && typeof encodedError === "string") {
            const loginError: LoginError = JSON.parse(decodeURIComponent(encodedError));
            const message = loginError.message;
            this.toggleError(true, message);
            return;
        }

        if (encodedAuthToken && typeof encodedAuthToken === "string") { // TODO replace it with strategy pattern
            this.logger.warning(`${encodedAuthToken}`, LoginFormView.name);
            const authToken = JSON.parse(decodeURIComponent(encodedAuthToken))
            this.setAuthToken(authToken);
            this.changeLoginState(LoginPageState.LoggedIn);
            this.setLoggedInUserRole(RoleType.BSCRep);
            this.logger.warning(`${authToken}`, LoginFormView.name);
            this.setSesionToken(authToken);
            this.handlePreConsentFlow();
            return;
        }
        // Reset corrupt token
        this.setSesionToken("");
    }

    render() {

        return <React.Fragment>
            <div className="LoginFormContent">
                {<LocationComponent onChange={this.handleLocationUpdate} />}
                {this.state.submitted && <Navigate to="/home" replace={true}></Navigate>}
                <Box
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                        display: "flex",
                        "& > :not(style)": {
                            m: 1,
                            border: "0px" //"1px dashed grey"
                        },
                    }}>
                    {this.state.loginState === LoginPageState.PostConsent &&  
                    <Paper variant="outlined"  >
                     <SelectClinicView onClinicSelected={this.onClinicSelected} />
                     </Paper>}
                    {this.state.loginState === LoginPageState.LoggedIn &&
                        <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center" style={{ width: "100%", height: "100%" }}
                            id="LoggedInBSCRep">
                            <Grid item xs={3}>
                                <AppProgressBar color="primary" />
                            </Grid>
                            <Grid item xs={3}>
                                <ThemeProvider theme={SsoLabelTheme}>
                                    <Typography className="SSOLabel">Fetching Clinics...</Typography>
                                </ThemeProvider>
                            </Grid>
                        </Grid>}
                    {this.state.loginState === LoginPageState.PreLogin && 
                    <Paper variant="outlined" className="SignInBox">
                        <ThemeProvider theme={SignLabelTheme}>
                            <Typography className="SignInLabel"> Sign In </Typography>
                        </ThemeProvider>
                        <ThemeProvider theme={roboto500Text16}>
                            <TextFieldWrapper id="outlined-basic" label="Enter your email" variant="outlined" onChange={(e) => { this.onChangeUsername(e.target.value) }}
                                className="UserNameTextField" size="medium" fullWidth
                                error={this.state.emailValidationError.length === 0 ? false : true} autoFocus
                                helperText={this.state.emailValidationError}/>
                        </ThemeProvider>
                        <FormControl className="PasswordTextField" variant="outlined" >
                             <ThemeProvider theme={roboto500Text16}>
                                <InputLabel htmlFor="outlined-adornment-password">Enter your password</InputLabel>
                            </ThemeProvider>
                            <OutlinedInput size="medium" autoComplete="off"
                                id="outlined-adornment-password"
                                style={{ fontSize: 16, borderRadius: "10px" }}
                                type={this.state.showPassword ? "text" : "password"}
                                onChange={(e) => { this.onChangePassword(e.target.value) }}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={this.handleClickShowPassword}
                                            onMouseDown={this.handleMouseDownPassword}
                                            edge="end"
                                        >
                                            {this.state.showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Enter your password"
                            />
                        </FormControl>
                        <Grid container justifyContent="end" alignItems="end" style={{ marginTop: "3rem" }}>
                            <ThemeProvider theme={SignButtonTheme}>
                                <Button className="ForgotPasswordButton" onClick={this.onForgotPasswordClicked}>Forgot Password?</Button>
                            </ThemeProvider>
                        </Grid>
                        <ThemeProvider theme={SignButtonTheme}>
                            <Button variant="contained" onClick={this.submitCredentials}
                                className="SignInButton" disabled={this.state.isSignInDisabled} disableElevation>
                                Sign In
                            </Button>
                        </ThemeProvider>

                        <Grid container justifyContent="center" alignItems="center" >
                            <Grid item xs={12}>
                                <ThemeProvider theme={SsoLabelTheme}>
                                    <Typography className="SSOLabel">Are you a Boston Scientific Rep?</Typography>
                                </ThemeProvider>
                            </Grid>

                            <Grid item xs={12} justifyContent="center" alignItems="center" container>
                                <ThemeProvider theme={SignButtonTheme}>
                                    <Button onClick={this.authenticate} className="SSOSignInButton" size="small">Sign In with BSC SSO</Button>
                                </ThemeProvider>
                            </Grid>

                        </Grid>
                    </Paper>
                    }
                </Box>
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={this.state.loading}>
                    <AppProgressBar color="primary" />
                </Backdrop>
                  <Dialog  open={this.state.showConsent}
                           fullScreen TransitionComponent={DialogTransition}>
                           <DialogContent sx={{padding: "0px"}} >
                              <HomeConsentDialog handleConsentNotice= {this.handleNoticeNotice}/>
                           </DialogContent>
                  </Dialog>
            </div>
            <CSSTransition
                in={this.state.errorToggle}
                timeout={300}
                classNames="alert"
                unmountOnExit>
                <Snackbar open={this.state.errorToggle} autoHideDuration={6000} onClose={()=> {this.toggleError(false, '');}}
                    anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                    <AppAlert onClose={()=>{ this.toggleError( false, '')}} severity="error" sx={{ width: "150%" }}>
                        {`${this.state.errorMessage}`}
                    </AppAlert>
                </Snackbar>
            </CSSTransition>
        </React.Fragment>
    }

}